import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { backend_api } from "../../../static/server";
import { set } from "date-fns";

// Get all users
export const getAllUsers = createAsyncThunk(
  "users/getAllUsers",
  async ({ token, page, pageSize }) => {
    try {
      let paginatedUrl = `${backend_api}team/create-employee/`;
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get users data");
      throw error.response?.data || error.message;
    }
  }
);

export const getAllRoles = createAsyncThunk(
  "users/getAllRoles",
  async ({ token, page, pageSize }) => {
    try {
      let paginatedUrl = `${backend_api}lawer/user-access/`;
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        const separator = paginatedUrl.includes("?") ? "&" : "?";
        paginatedUrl = `${paginatedUrl}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get roles data");
      throw error.response?.data || error.message;
    }
  }
);

export const getUserAccess = createAsyncThunk(
  "users/getUserAccess",
  async ({ token, userId }) => {
    try {
      const response = await axios.get(
        `${backend_api}lawer/user-access/${userId}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      console.log(response);
      return response.data;
    } catch (error) {
      toast.error("Failed to get user access data");
      throw error.response?.data || error.message;
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    // Users data
    allUsers: null,
    usersLoading: false,
    usersError: null,
    userPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },

    teamData: null,
    teamMemberLawyerId: null,
    // Roles data
    allRoles: null,
    rolesLoading: false,
    rolesError: null,
    rolesPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },

    // User auth and access
    token: null,
    user: null,
    lawerId: null,
    userId: null,
    userAccess: null,
    accessLoading: false,
    accessError: null,
  },
  reducers: {
    setUser: (state, action) => {
      console.log("setUser action payload:", action.payload);
      state.user = action.payload;
    },
    setTeamData: (state, action) => {
      state.teamData = action.payload;
      console.log("setTeamData action payload:", action.payload);
    },
    setTeamMemberLawyerId: (state, action) => {
      state.teamMemberLawyerId = action.payload;
      console.log("setTeamMemberLawyerId action payload:", action.payload);
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setUserAccess: (state, action) => {
      state.userAccess = action.payload;
    },
    setLawerId: (state, action) => {
      state.lawerId = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setUserPagination: (state, action) => {
      state.userPagination = { ...state.userPagination, ...action.payload };
    },
    setRolesPagination: (state, action) => {
      state.rolesPagination = { ...state.rolesPagination, ...action.payload };
    },
    logout: (state) => {
      state.token = null;
      state.teamData = null;
      state.user = null;
      state.lawerId = null;
      state.userId = null;
      state.allUsers = null;
      state.allRoles = null;
      state.userAccess = null;
      state.userPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
      state.rolesPagination = {
        page: 1,
        pageSize: 10,
        rowCount: 0,
        nextPage: null,
        previousPage: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Users cases
      .addCase(getAllUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.allUsers = action.payload.results;
        state.userPagination = {
          ...state.userPagination,
          page: action.payload.pagination.page,
          pageSize: action.payload.pagination.pageSize,
          rowCount: action.payload.count,
          nextPage: action.payload.next,
          previousPage: action.payload.previous,
        };
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      })

      // Roles cases
      .addCase(getAllRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.allRoles = action.payload.results;
        state.rolesPagination = {
          ...state.rolesPagination,
          page: action.payload.pagination.page,
          pageSize: action.payload.pagination.pageSize,
          rowCount: action.payload.count,
          nextPage: action.payload.next,
          previousPage: action.payload.previous,
        };
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      })

      // User access cases
      .addCase(getUserAccess.pending, (state) => {
        state.accessLoading = true;
        state.accessError = null;
      })
      .addCase(getUserAccess.fulfilled, (state, action) => {
        state.accessLoading = false;
        state.userAccess = action.payload;
      })
      .addCase(getUserAccess.rejected, (state, action) => {
        state.accessLoading = false;
        state.accessError = action.payload;
      });
  },
});

export const {
  setUser,
  setTeamData,
  setTeamMemberLawyerId,
  setToken,
  setLawerId,
  setUserId,
  setUserAccess,
  setUserPagination,
  setRolesPagination,
  logout,
} = userSlice.actions;

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "token",
    "userId",
    "lawerId",
    "userAccess",
    "teamData",
    "teamMemberLawyerId",
  ],
};

export const persistedReducer = persistReducer(
  persistConfig,
  userSlice.reducer
);
